import { get, patch, post, put, remove } from "../../api/api";

export type GtmSiteSourceStatus = "gained" | "suggesting" | "suggested" | "error";
export type GtmBrainDocType =
  | "persona"
  | "pillar"
  | "objection"
  | "competitor"
  | "influencer"
  | "keyword";
export type GtmBrainReviewVerdict = "pass" | "fail" | "skipped" | "error";

export interface GtmBrain {
  id?: string;
  valueProposition?: string;
  businessSummary?: string;
  offering?: string;
  keyFeatures?: string;
  socialProof?: string;
  industries?: string[];
  geographies?: string[];
  icpSummary?: string;
  targetCompanySize?: string;
  verticals?: string[];
  buyingRoles?: string[];
  championRoles?: string[];
  userRoles?: string[];
  companyName?: string;
  domain?: string;
  linkedinUrl?: string;
  companySize?: string;
  businessModel?: string;
  stage?: string;
  language?: string;
  toneOfVoice?: string;
  voiceLinkedin?: string;
  voiceBlog?: string;
  signalsJson?: string;
  calendarUrl?: string;
  sourceFetchId?: string | null;
}

export interface GtmBrainDoc {
  id?: string;
  docType: GtmBrainDocType;
  name?: string | null;
  seniority?: string | null;
  description?: string | null;
  topPains?: string[];
  wateringHoles?: string[];
  pain?: string | null;
  valueProposition?: string | null;
  proof?: string | null;
  objection?: string | null;
  response?: string | null;
  domain?: string | null;
  linkedinUrl?: string | null;
  notes?: string | null;
  phrase?: string | null;
}

export interface GtmBrainReviewIssue {
  path?: string;
  reason?: string;
}

export interface GtmBrainLatestReview {
  fetchId?: string;
  verdict?: GtmBrainReviewVerdict;
  issues?: GtmBrainReviewIssue[];
}

export interface GtmSiteSourceSummary {
  fetchId?: string;
  url?: string;
  status?: GtmSiteSourceStatus;
  errorMessage?: string | null;
}

export interface GtmBrainResponse {
  brain: GtmBrain | null;
  docs: GtmBrainDoc[];
  latestReview: GtmBrainLatestReview | null;
  siteSource: GtmSiteSourceSummary | null;
}

export async function getGtmBrain(workspaceId: string): Promise<GtmBrainResponse> {
  const { data } = await get({ path: `/${workspaceId}/gtm/brain` });
  return normalizeBrainResponse(data);
}

export async function fetchGtmBrain(
  workspaceId: string,
  url: string
): Promise<{ fetchId: string }> {
  const { data } = await post({
    path: `/${workspaceId}/gtm/brain/fetch`,
    data: { url },
  });
  return data;
}

export async function patchGtmBrain(
  workspaceId: string,
  body: Partial<GtmBrain>
): Promise<GtmBrainResponse> {
  const { data } = await patch({ path: `/${workspaceId}/gtm/brain`, data: body });
  return normalizeBrainResponse(data);
}

export async function replaceGtmBrainDocs(
  workspaceId: string,
  docs: GtmBrainDoc[]
): Promise<GtmBrainResponse> {
  const { data } = await put({
    path: `/${workspaceId}/gtm/brain/docs`,
    data: { docs },
  });
  return normalizeBrainResponse(data);
}

function normalizeBrainResponse(data: any): GtmBrainResponse {
  return {
    brain: data?.brain ?? null,
    docs: Array.isArray(data?.docs) ? data.docs : [],
    latestReview: data?.latestReview ?? null,
    siteSource: data?.siteSource ?? null,
  };
}

export type GtmPlayStatus = "draft" | "active" | "paused";

export interface GtmPlay {
  id: string;
  name: string;
  audience: string;
  angle: string;
  status: GtmPlayStatus;
  createdAt?: string;
  updatedAt?: string;
}

export interface GtmSequenceTemplate {
  id?: string;
  playId: string;
  definitionJson: string;
}

export async function listGtmPlays(
  workspaceId: string,
  status?: GtmPlayStatus | "all"
): Promise<GtmPlay[]> {
  const params = status && status !== "all" ? { status } : undefined;
  const { data } = await get({ path: `/${workspaceId}/gtm/plays`, params });
  return Array.isArray(data) ? data : [];
}

export async function getGtmPlay(workspaceId: string, playId: string): Promise<GtmPlay> {
  const { data } = await get({ path: `/${workspaceId}/gtm/plays/${playId}` });
  return data;
}

export async function createGtmPlay(
  workspaceId: string,
  body: { audience: string; name?: string }
): Promise<GtmPlay> {
  const { data } = await post({
    path: `/${workspaceId}/gtm/plays`,
    data: body,
    timeoutMs: 120_000,
  });
  return data;
}

export async function patchGtmPlay(
  workspaceId: string,
  playId: string,
  body: Partial<Pick<GtmPlay, "name" | "audience" | "angle" | "status">>
): Promise<GtmPlay> {
  const { data } = await patch({ path: `/${workspaceId}/gtm/plays/${playId}`, data: body });
  return data;
}

export async function deleteGtmPlay(workspaceId: string, playId: string): Promise<void> {
  await remove({ path: `/${workspaceId}/gtm/plays/${playId}` });
}

export async function getGtmSequenceTemplate(
  workspaceId: string,
  playId: string
): Promise<GtmSequenceTemplate> {
  const { data } = await get({ path: `/${workspaceId}/gtm/plays/${playId}/sequence-template` });
  return data;
}

export type GtmSequenceMessageMode = "ai" | "custom";

export type GtmSequenceMessageStepPatch = {
  step: string;
  messageMode?: GtmSequenceMessageMode;
  messageBody?: string;
};

export type GtmSequenceTemplateMessagePatch = {
  messages: GtmSequenceMessageStepPatch[];
};

export async function patchGtmSequenceTemplate(
  workspaceId: string,
  playId: string,
  body: GtmSequenceTemplateMessagePatch
): Promise<GtmSequenceTemplate> {
  const { data } = await patch({
    path: `/${workspaceId}/gtm/plays/${playId}/sequence-template`,
    data: body,
  });
  return data;
}

export interface GtmSequenceStepPerson {
  leadId: string;
  name: string;
  status: string;
  currentStep: string;
  nextActionAt?: string | null;
}

export interface GtmSequenceStepEvent {
  id: string;
  leadId: string;
  leadName: string;
  step: string;
  kind: string;
  body: string;
  createdAt: string;
}

export interface GtmSequenceNodeActivity {
  stepKey: string;
  people: GtmSequenceStepPerson[];
  events: GtmSequenceStepEvent[];
}

export async function getGtmSequenceNodeActivity(
  workspaceId: string,
  playId: string,
  step: string,
  limit = 40
): Promise<GtmSequenceNodeActivity> {
  const { data } = await get({
    path: `/${workspaceId}/gtm/plays/${playId}/sequence-activity`,
    params: { step, limit },
  });
  return data;
}

export type GtmLinkedInSessionStatus = "connected" | "rejected" | "missing";
export type GtmLinkedInSequenceState = "running" | "paused";

export interface GtmSessionStatus {
  status: GtmLinkedInSessionStatus;
  sequence: GtmLinkedInSequenceState;
}

export async function getGtmSession(workspaceId: string): Promise<GtmSessionStatus> {
  const { data } = await get({ path: `/${workspaceId}/gtm/session` });
  return data;
}

export async function saveGtmSession(
  workspaceId: string,
  body: { liAt: string; tosAcknowledged: boolean }
): Promise<GtmSessionStatus> {
  const { data } = await post({
    path: `/${workspaceId}/gtm/session`,
    data: body,
    timeoutMs: 60_000,
  });
  return data;
}

export async function disconnectGtmSession(workspaceId: string): Promise<GtmSessionStatus> {
  const { data } = await remove({ path: `/${workspaceId}/gtm/session` });
  return data;
}

export async function patchGtmSessionSequence(
  workspaceId: string,
  sequence: GtmLinkedInSequenceState
): Promise<GtmSessionStatus> {
  const { data } = await patch({
    path: `/${workspaceId}/gtm/session/sequence`,
    data: { sequence },
  });
  return data;
}

export type GtmLeadSearchStatus = "running" | "ready" | "error";
export type GtmLeadCandidateStatus = "found" | "added" | "dismissed";

export interface GtmLeadSearch {
  id: string;
  playId: string;
  queryText?: string;
  status: GtmLeadSearchStatus;
  errorMessage?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface GtmLeadCandidate {
  id: string;
  playId: string;
  searchId: string;
  profileSlug: string;
  name: string;
  title: string;
  company: string;
  location?: string;
  whyFit?: string;
  linkedinUrl: string;
  avatarUrl?: string;
  status: GtmLeadCandidateStatus;
}

export interface GtmLead {
  id: string;
  playId: string;
  profileSlug: string;
  name: string;
  title: string;
  company: string;
  location?: string;
  whyFit?: string;
  linkedinUrl: string;
  avatarUrl?: string;
  status: string;
  connected?: string | null;
  email?: string | null;
  contactId?: string | null;
  companyId?: string | null;
  calendarUrl?: string | null;
}

export async function findGtmLeads(workspaceId: string, playId: string): Promise<GtmLeadSearch> {
  const { data } = await post({
    path: `/${workspaceId}/gtm/plays/${playId}/find-leads`,
    data: {},
    timeoutMs: 30_000,
  });
  return data;
}

export async function getGtmLatestLeadSearch(
  workspaceId: string,
  playId: string
): Promise<GtmLeadSearch | null> {
  const { data } = await get({ path: `/${workspaceId}/gtm/plays/${playId}/lead-search` });
  return data?.search ?? null;
}

export async function listGtmCandidates(
  workspaceId: string,
  playId: string,
  status?: "found" | "dismissed"
): Promise<GtmLeadCandidate[]> {
  const params = status ? { status } : undefined;
  const { data } = await get({ path: `/${workspaceId}/gtm/plays/${playId}/candidates`, params });
  return Array.isArray(data) ? data : [];
}

export async function addGtmCandidate(
  workspaceId: string,
  candidateId: string
): Promise<GtmLead> {
  const { data } = await post({
    path: `/${workspaceId}/gtm/candidates/${candidateId}/add`,
    data: {},
  });
  return data;
}

export async function dismissGtmCandidate(
  workspaceId: string,
  candidateId: string
): Promise<GtmLeadCandidate> {
  const { data } = await post({
    path: `/${workspaceId}/gtm/candidates/${candidateId}/dismiss`,
    data: {},
  });
  return data;
}

export async function enrichGtmCandidate(
  workspaceId: string,
  candidateId: string
): Promise<GtmLeadCandidate> {
  const { data } = await post({
    path: `/${workspaceId}/gtm/candidates/${candidateId}/enrich`,
    data: {},
  });
  return data;
}

export async function enrichGtmLead(
  workspaceId: string,
  leadId: string
): Promise<GtmLead> {
  const { data } = await post({
    path: `/${workspaceId}/gtm/leads/${leadId}/enrich`,
    data: {},
  });
  return data;
}

export type GtmLeadStatusFilter =
  | "approve"
  | "in_sequence"
  | "waiting"
  | "review"
  | "hands"
  | "meeting"
  | "ended"
  | "needs_you"
  | "in_sequence_tab";

export async function listGtmLeads(
  workspaceId: string,
  playId: string,
  status?: GtmLeadStatusFilter
): Promise<GtmLead[]> {
  const params = status ? { status } : undefined;
  const { data } = await get({ path: `/${workspaceId}/gtm/plays/${playId}/leads`, params });
  return Array.isArray(data) ? data : [];
}

export async function getGtmApproveRiskRequired(workspaceId: string): Promise<boolean> {
  const { data } = await get({ path: `/${workspaceId}/gtm/approve-risk-required` });
  return !!data?.required;
}

export interface GtmLeadApproveResult {
  leadId: string;
  contactId?: string | null;
  companyId?: string | null;
  enrollmentId: string;
  sequencePaused: boolean;
}

export async function approveGtmLead(
  workspaceId: string,
  leadId: string,
  body: { email: string; riskAcknowledged?: boolean }
): Promise<GtmLeadApproveResult> {
  const { data } = await post({
    path: `/${workspaceId}/gtm/leads/${leadId}/approve`,
    data: body,
  });
  return data;
}

export interface GtmLeadGateResult {
  leadId: string;
  status: string;
  enrollmentId?: string | null;
  nextActionAt?: string | null;
  calendarUrl?: string | null;
}

export async function completeGtmLeadReview(
  workspaceId: string,
  leadId: string
): Promise<GtmLeadGateResult> {
  const { data } = await post({
    path: `/${workspaceId}/gtm/leads/${leadId}/complete-review`,
    data: {},
  });
  return data;
}

export async function stopGtmLeadSequence(
  workspaceId: string,
  leadId: string
): Promise<GtmLeadGateResult> {
  const { data } = await post({
    path: `/${workspaceId}/gtm/leads/${leadId}/stop`,
    data: {},
  });
  return data;
}

export type GtmLeadSequenceMessageSource = "override" | "play_custom" | "ai" | "sent";

export interface GtmLeadSequenceLead {
  id: string;
  name: string;
  title: string;
  company: string;
  location?: string;
  whyFit?: string;
  linkedinUrl?: string;
  avatarUrl?: string;
  status: string;
  connected?: string;
  email?: string | null;
  contactId?: string | null;
}

export interface GtmLeadSequenceStep {
  id: string;
  step: string;
  kind: string;
  label: string;
  status: string;
  body?: string | null;
  source?: GtmLeadSequenceMessageSource | null;
  sentBody?: string | null;
  editable?: boolean;
  /** ISO local datetime when this step ran or is estimated to run. */
  estimatedAt?: string | null;
}

export interface GtmLeadSequence {
  lead: GtmLeadSequenceLead;
  enrollmentId: string;
  currentStep: string;
  nextActionAt?: string | null;
  statusBlurb: string;
  steps: GtmLeadSequenceStep[];
}

export async function getGtmLeadSequence(
  workspaceId: string,
  leadId: string
): Promise<GtmLeadSequence> {
  const { data } = await get({ path: `/${workspaceId}/gtm/leads/${leadId}/sequence` });
  return data;
}

export async function draftGtmLeadSequenceMessages(
  workspaceId: string,
  leadId: string
): Promise<GtmLeadSequence> {
  const { data } = await post({
    path: `/${workspaceId}/gtm/leads/${leadId}/sequence/draft-messages`,
    data: {},
    timeoutMs: 120_000,
  });
  return data;
}

export async function patchGtmLeadSequenceMessages(
  workspaceId: string,
  leadId: string,
  body: { messages: { step: string; body: string }[] }
): Promise<GtmLeadSequence> {
  const { data } = await patch({
    path: `/${workspaceId}/gtm/leads/${leadId}/sequence/messages`,
    data: body,
  });
  return data;
}

export async function ensureGtmSequenceRule(
  workspaceId: string
): Promise<{ ruleId: string | null }> {
  const { data } = await post({
    path: `/${workspaceId}/gtm/sequence/ensure-rule`,
    data: {},
  });
  return { ruleId: data?.ruleId ?? null };
}
